// The collaboration client.
//
// Talks to the board WebSocket and keeps the local Excalidraw scene in step with
// everyone else's. The merge itself is Excalidraw's own `reconcileElements`, so
// this file is only responsible for deciding what to send, when to send it, and
// what to do with what comes back.
//
// Two rules keep it from talking to itself:
//   - We remember the version of every element we have sent or received. An
//     element is only sent when its version has moved past that.
//   - Remote elements are applied with CaptureUpdateAction.NEVER, so somebody
//     else's edit never lands in your undo stack.
//
// And one rule keeps work from being lost: nothing is considered safe until the
// server says it wrote it. Until then the scene is mirrored to IndexedDB, and
// on the next open anything the server never received is merged back and
// re-sent. See collab/backup.js.

import { CaptureUpdateAction, reconcileElements, restoreElements } from "@excalidraw/excalidraw";
import { withComments } from "@hivemind-canvas/shared/comments";
import { canDropBackup, unsyncedElements } from "@hivemind-canvas/shared/recovery";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api.js";
import { clearBackup, readBackup, saveBackup } from "./backup.js";

const SEND_INTERVAL_MS = 150;
const POINTER_INTERVAL_MS = 60;
const BACKUP_INTERVAL_MS = 3_000;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10_000;

const socketUrl = (boardId) => {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws?board=${encodeURIComponent(boardId)}`;
};

/** Excalidraw wants both a stroke and a background for a cursor colour. */
const collaboratorColor = (color) => ({ background: color, stroke: color });

const SAFE = { state: "saved", at: null, pending: 0, error: null, recovered: 0 };

const selectedIdsOf = (appState) =>
  Object.entries(appState?.selectedElementIds ?? {})
    .filter(([, on]) => on)
    .map(([id]) => id);

export const useCollab = ({ boardId, excalidrawAPI }) => {
  const [status, setStatus] = useState("connecting");
  const [collaborators, setCollaborators] = useState([]);
  const [me, setMe] = useState(null);
  const [save, setSave] = useState(SAFE);
  // False until the first scene has arrived. The canvas is genuinely empty
  // before that, and showing an empty board that fills in a moment later reads
  // as a glitch.
  const [ready, setReady] = useState(false);
  const [comments, setComments] = useState([]);

  const socketRef = useRef(null);
  const versionsRef = useRef(new Map()); // elementId -> version we have already exchanged
  const outboxRef = useRef(new Map()); // elementId -> element waiting to be sent
  const peopleRef = useRef(new Map()); // clientId -> {name, color, pointer, button, selectedIds}
  const uploadedFilesRef = useRef(new Set());
  const lastPointerRef = useRef(0); // when we last sent a pointer frame
  const pointerAtRef = useRef(null); // where the pointer last was, in scene units
  const selectedRef = useRef([]); // what we have selected, sent along with the pointer
  const closedRef = useRef(false);
  const myIdRef = useRef(null); // our own client id, so presence never renders us twice
  // True from the moment we change something until the server says it is on
  // disk. It is what decides whether a local backup is kept.
  const unconfirmedRef = useRef(false);

  /** Push the current peer list into Excalidraw so it draws the cursors. */
  const paintCollaborators = useCallback(() => {
    if (!excalidrawAPI) return;
    const map = new Map();
    for (const [id, person] of peopleRef.current) {
      map.set(id, {
        id,
        username: person.name,
        color: collaboratorColor(person.color),
        ...(person.pointer ? { pointer: { ...person.pointer, tool: "pointer" } } : {}),
        button: person.button ?? "up",
        selectedElementIds: person.selectedIds ?? {},
      });
    }
    excalidrawAPI.updateScene({ collaborators: map });
    setCollaborators([...peopleRef.current.entries()].map(([id, p]) => ({ id, ...p })));
  }, [excalidrawAPI]);

  /**
   * One pointer frame: where we are and what we have selected. The server keeps
   * the last one per person, which is how an agent learns what "this box" means.
   * A change of selection goes out at once, without waiting for the mouse to move.
   */
  const sendPointer = useCallback((button, { force = false } = {}) => {
    const socket = socketRef.current;
    const at = pointerAtRef.current;
    if (socket?.readyState !== WebSocket.OPEN || !at) return;

    const now = Date.now();
    if (!force && now - lastPointerRef.current < POINTER_INTERVAL_MS) return;
    lastPointerRef.current = now;

    socket.send(
      JSON.stringify({ t: "pointer", x: at.x, y: at.y, button, selectedIds: selectedRef.current.slice(0, 500) }),
    );
  }, []);

  const mergeComments = useCallback(
    (incoming) => setComments((current) => withComments(current, incoming)),
    [],
  );

  const rememberVersions = (elements) => {
    for (const el of elements) versionsRef.current.set(el.id, el.version);
  };

  /**
   * Merge elements from the server into the local scene.
   *
   * `repairBindings` may only be used on a COMPLETE scene. An incremental update
   * carries just the elements that changed, and repairing bindings against that
   * partial set drops every reference it cannot resolve inside the batch: recolour
   * one labelled shape and the label vanishes, because its container no longer
   * lists it. So it is on for `init` and off for every update after that.
   */
  const applyRemote = useCallback(
    (remote, { repairBindings = false } = {}) => {
      if (!excalidrawAPI || remote.length === 0) return;
      const restored = restoreElements(remote, null, { repairBindings });
      const merged = reconcileElements(
        excalidrawAPI.getSceneElementsIncludingDeleted(),
        restored,
        excalidrawAPI.getAppState(),
      );
      rememberVersions(restored);
      excalidrawAPI.updateScene({
        elements: merged,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    },
    [excalidrawAPI],
  );

  // ---- the connection ----

  useEffect(() => {
    if (!boardId || !excalidrawAPI) return undefined;

    closedRef.current = false;
    setReady(false);
    setComments([]);
    let socket;
    let reconnectTimer;
    let attempt = 0;

    // The backup must not be cleared before it has been read. The server's very
    // first save frame says "saved, nothing pending" — which is true of the
    // server and says nothing about what this browser is still holding — so
    // acting on it early would delete the recovery copy on every board open,
    // which is exactly the moment it exists to be used.
    let recoveryDone = false;
    // Kept so the last verdict can be re-applied once recovery finishes: a
    // quiet board may not produce another save frame for a long time, and the
    // status must not sit at "saving…" until it does.
    let lastSaveFrame = null;

    /**
     * Work this browser still holds that the server never got: an edit made
     * while the connection was down, in a tab that was then closed. Merged in
     * and queued for sending on every init, including a reconnect.
     */
    const restoreUnsyncedWork = async (serverElements) => {
      try {
        const backup = await readBackup(boardId);
        if (!backup?.elements?.length) return;

        const owed = unsyncedElements(backup.elements, serverElements);
        if (owed.length === 0) {
          void clearBackup(boardId);
          return;
        }

        applyRemote(owed);
        for (const el of owed) outboxRef.current.set(el.id, el);
        unconfirmedRef.current = true;
        setSave((s) => ({ ...s, state: "saving", recovered: owed.length }));
        // eslint-disable-next-line no-console
        console.info(`[canvas] restored ${owed.length} element(s) this browser had not managed to send`);
      } catch (err) {
        console.warn("[canvas] could not read the local backup", err);
      } finally {
        recoveryDone = true;
        if (lastSaveFrame) applySaveFrame(lastSaveFrame);
      }
    };

    const open = () => {
      setStatus(attempt === 0 ? "connecting" : "reconnecting");
      socket = new WebSocket(socketUrl(boardId));
      socketRef.current = socket;

      socket.onopen = () => {
        attempt = 0;
        setStatus("live");
      };

      socket.onmessage = (event) => {
        let frame;
        try {
          frame = JSON.parse(event.data);
        } catch {
          return;
        }

        if (frame.t === "init") {
          versionsRef.current.clear();
          myIdRef.current = frame.you.id;
          setMe(frame.you);
          peopleRef.current = new Map(
            frame.collaborators.filter((c) => c.id !== frame.you.id).map((c) => [c.id, { ...c }]),
          );
          applyRemote(frame.elements, { repairBindings: true });
          paintCollaborators();
          recoveryDone = false;
          void restoreUnsyncedWork(frame.elements);
          if (frame.save) applySaveFrame(frame.save);
          // Frame the drawing on first load, not on a reconnect mid-session.
          if (frame.elements.length > 0 && attempt === 0) {
            requestAnimationFrame(() => excalidrawAPI.scrollToContent(undefined, { fitToContent: true }));
          }
          void loadFiles();
          void loadComments();
          setReady(true);
          // The server starts every connection with nothing selected, so say
          // again what we have selected, or an agent reading the board after a
          // reconnect would see an empty selection until the mouse moves.
          sendPointer("up", { force: true });
        } else if (frame.t === "update") {
          applyRemote(frame.elements);
        } else if (frame.t === "comment") {
          mergeComments([frame.comment]);
        } else if (frame.t === "save") {
          applySaveFrame(frame);
        } else if (frame.t === "presence") {
          const next = new Map();
          for (const person of frame.collaborators) {
            if (person.id === myIdRef.current) continue;
            const existing = peopleRef.current.get(person.id);
            next.set(person.id, { ...existing, ...person });
          }
          peopleRef.current = next;
          paintCollaborators();
        } else if (frame.t === "pointer") {
          const existing = peopleRef.current.get(frame.id) ?? {};
          peopleRef.current.set(frame.id, {
            ...existing,
            name: frame.name,
            color: frame.color,
            pointer: { x: frame.x, y: frame.y },
            button: frame.button,
            selectedIds: Object.fromEntries((frame.selectedIds ?? []).map((id) => [id, true])),
          });
          paintCollaborators();
        } else if (frame.t === "files") {
          void loadFiles();
        } else if (frame.t === "error") {
          console.warn("[canvas] server rejected a frame:", frame.error);
        }
      };

      socket.onclose = () => {
        if (closedRef.current) return;
        setStatus("offline");
        peopleRef.current.clear();
        paintCollaborators();
        attempt += 1;
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempt - 1), RECONNECT_MAX_MS);
        reconnectTimer = setTimeout(open, delay);
      };

      socket.onerror = () => socket.close();
    };

    /**
     * The server's word on whether the board is on disk. Only a clean, complete
     * save clears the local backup: anything else and this browser stays the
     * one place some of the work exists.
     */
    const applySaveFrame = (frame) => {
      lastSaveFrame = frame;
      // All of: the server has written everything, this browser has nothing
      // queued, and the recovery pass has finished looking. See canDropBackup
      // for why the third one is not optional.
      const clean = canDropBackup({
        saved: frame.saved,
        error: frame.error,
        queued: outboxRef.current.size,
        recoveryDone,
      });
      unconfirmedRef.current = !clean;
      setSave((s) => ({
        ...s,
        state: frame.error ? "failing" : clean ? "saved" : "saving",
        at: frame.at ?? s.at,
        pending: frame.pending ?? 0,
        error: frame.error ?? null,
      }));
      if (clean) void clearBackup(boardId);
    };

    const loadFiles = async () => {
      try {
        const { files } = await api.getFiles(boardId);
        const list = Object.values(files ?? {});
        if (list.length) {
          for (const file of list) uploadedFilesRef.current.add(file.id);
          excalidrawAPI.addFiles(list);
        }
      } catch (err) {
        console.warn("[canvas] could not load board images", err);
      }
    };

    // On every init, so a reconnect also picks up what was said while we were away.
    const loadComments = async () => {
      try {
        mergeComments((await api.listComments(boardId)).comments);
      } catch (err) {
        console.warn("[canvas] could not load comments", err);
      }
    };

    open();

    // Flush queued element changes on a fixed cadence rather than per keystroke.
    const flush = setInterval(() => {
      const outbox = outboxRef.current;
      if (outbox.size === 0) return;
      if (socketRef.current?.readyState !== WebSocket.OPEN) {
        // Nothing is going anywhere while the socket is down. Say so rather
        // than showing a stale "saved".
        setSave((s) => (s.state === "unsent" ? s : { ...s, state: "unsent" }));
        return;
      }
      const elements = [...outbox.values()];
      outbox.clear();
      socketRef.current.send(JSON.stringify({ t: "update", elements }));
    }, SEND_INTERVAL_MS);

    // Mirror the scene while anything is unconfirmed, and only then: a board
    // sitting safely on disk does not need a second copy in the browser.
    const backup = setInterval(() => {
      if (!unconfirmedRef.current || !excalidrawAPI) return;
      void saveBackup(boardId, excalidrawAPI.getSceneElementsIncludingDeleted());
    }, BACKUP_INTERVAL_MS);

    // Closing a tab with unsent work is the one case worth interrupting.
    const onBeforeUnload = (event) => {
      if (!unconfirmedRef.current) return;
      if (excalidrawAPI) void saveBackup(boardId, excalidrawAPI.getSceneElementsIncludingDeleted());
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      closedRef.current = true;
      clearInterval(flush);
      clearInterval(backup);
      clearTimeout(reconnectTimer);
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (unconfirmedRef.current && excalidrawAPI) {
        void saveBackup(boardId, excalidrawAPI.getSceneElementsIncludingDeleted());
      }
      socket?.close();
      socketRef.current = null;
      outboxRef.current.clear();
      versionsRef.current.clear();
      peopleRef.current.clear();
    };
  }, [boardId, excalidrawAPI, applyRemote, paintCollaborators, mergeComments, sendPointer]);

  // ---- what we send ----

  const onChange = useCallback(
    (elements, appState, files) => {
      const versions = versionsRef.current;
      let changed = false;
      for (const el of elements) {
        if (versions.get(el.id) !== el.version) {
          versions.set(el.id, el.version);
          outboxRef.current.set(el.id, el);
          changed = true;
        }
      }
      if (changed) {
        unconfirmedRef.current = true;
        setSave((s) => (s.state === "saved" ? { ...s, state: "saving" } : s));
      }

      const selected = selectedIdsOf(appState);
      if (selected.join() !== selectedRef.current.join()) {
        selectedRef.current = selected;
        sendPointer("up", { force: true });
      }

      void uploadNewFiles(files, boardId, uploadedFilesRef.current);
    },
    [boardId, sendPointer],
  );

  const onPointerUpdate = useCallback(
    (payload) => {
      if (!payload?.pointer) return;
      pointerAtRef.current = { x: payload.pointer.x, y: payload.pointer.y };
      sendPointer(payload.button === "down" ? "down" : "up");
    },
    [sendPointer],
  );

  // Both go through the room, which tells everyone else on the board, us included.
  const addComment = useCallback(
    async (payload) => {
      const { comment } = await api.addComment(boardId, payload);
      mergeComments([comment]);
      return comment;
    },
    [boardId, mergeComments],
  );

  const setResolved = useCallback(
    async (commentId, resolved) => {
      const { comment } = await api.resolveComment(boardId, commentId, resolved);
      mergeComments([comment]);
      return comment;
    },
    [boardId, mergeComments],
  );

  return { status, ready, collaborators, me, save, comments, addComment, setResolved, onChange, onPointerUpdate };
};

/**
 * Images live in S3, not in the scene JSON, so anything pasted onto the board is
 * uploaded once and then referenced by id.
 */
const uploadNewFiles = async (files, boardId, alreadyUploaded) => {
  if (!files) return;
  const fresh = {};
  for (const [id, file] of Object.entries(files)) {
    if (!alreadyUploaded.has(id) && file?.dataURL) {
      fresh[id] = { mimeType: file.mimeType, dataURL: file.dataURL, created: file.created };
      alreadyUploaded.add(id);
    }
  }
  if (Object.keys(fresh).length === 0) return;
  try {
    await api.putFiles(boardId, fresh);
  } catch (err) {
    for (const id of Object.keys(fresh)) alreadyUploaded.delete(id); // let a retry happen
    console.warn("[canvas] could not upload a pasted image", err);
  }
};
